// src/components/Button/Button.jsx
import React from "react";

/* join classes */
const cx = (...s) => s.filter(Boolean).join(" ");

const VARIANTS = {
  brand:
    "bg-gradient-to-r from-sky-500 via-violet-500 to-orange-400 text-white " +
    "hover:opacity-95 shadow-sm hover:shadow-md " +
    "dark:from-sky-400 dark:via-violet-500 dark:to-rose-500",
  primary:
    "bg-primary text-white border border-primary hover:bg-white hover:text-primary",
  outline:
    "bg-transparent border border-primary text-primary hover:bg-primary/10 dark:hover:bg-white/10",
  soft:
    "bg-primary/10 text-primary hover:bg-primary/15 " +
    "dark:bg-white/10 dark:text-white dark:hover:bg-white/15",
  ghost:
    "bg-transparent text-primary hover:bg-primary/10 dark:text-white dark:hover:bg-white/10",
  success: "bg-emerald-600 text-white hover:bg-emerald-500",
  danger: "bg-red-600 text-white hover:bg-red-500",
  link: "bg-transparent text-primary hover:underline dark:text-white",
};

const SIZES = {
  xs: "h-8 px-3 text-xs",
  sm: "h-9 px-4 text-sm",
  md: "h-10 px-5 text-sm",
  lg: "h-12 px-6 text-base",
};

const ICON_SIZES = {
  xs: "text-sm",
  sm: "text-base",
  md: "text-lg",
  lg: "text-xl",
};

const Spinner = ({ size = "md" }) => (
  <svg
    className={cx("animate-spin", size === "lg" ? "h-5 w-5" : "h-4 w-4")}
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4z"
    />
  </svg>
);

/**
 * Button polymorphe : button | a | composant (Link/NavLink)
 *
 * Props compat héritées :
 * - text, icon (gauche), hoverLabel, className, onClick
 *
 * Nouvelles props :
 * - children, iconRight, variant, size, as, href, to, target, rel, type, loading, disabled, fullWidth, ariaLabel
 */
const Button = React.forwardRef(
  (
    {
      // Compat
      text,
      icon: IconLeft,
      hoverLabel = "",
      // New
      children,
      iconRight: IconRight,
      variant = "brand", // brand | primary | outline | soft | ghost | success | danger | link
      size = "md", // xs | sm | md | lg
      as, // "button" (defaut) | "a" | ReactComponent (Link/NavLink...)
      href,
      to, // pour Link/NavLink
      target,
      rel,
      type = "button",
      loading = false,
      disabled = false,
      fullWidth = false,
      className = "",
      ariaLabel, // prioritaire sur hoverLabel
      onClick,
      ...rest
    },
    ref
  ) => {
    // Détermine le rendu : string tag ou composant
    const Tag = as || (href ? "a" : "button"); // si href fourni sans `as`, on rend <a>, sinon <button>

    const isButtonTag = Tag === "button";
    const isAnchorTag = Tag === "a";

    const label =
      ariaLabel || hoverLabel || (typeof text === "string" ? text : undefined);

    const content = children ?? text;
    const isIconOnly = !content && (IconLeft || IconRight);
    const finalDisabled = disabled || loading;

    const base =
      "inline-flex items-center justify-center gap-2 rounded-2xl font-semibold tracking-wide select-none " +
      "transition-transform duration-200 focus:outline-none " +
      "focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 " +
      "disabled:opacity-60 disabled:cursor-not-allowed active:scale-[0.98] " +
      "shadow-sm";
    const sizeClass = SIZES[size] || SIZES.md;
    const variantClass = VARIANTS[variant] || VARIANTS.brand;
    const widthClass = fullWidth ? "w-full" : "w-auto";
    const iconClass = ICON_SIZES[size] || ICON_SIZES.md;

    // A11y : si icône seule, exige un aria-label
    const a11yProps = isIconOnly && !label ? { "aria-label": "Bouton" } : {};

    // Props dynamiques selon le type
    const elementProps = {
      ref,
      onClick,
      className: cx(base, sizeClass, variantClass, widthClass, className),
      "aria-label": label,
      title: hoverLabel || undefined,
      ...a11yProps,
      ...rest,
    };

    // Button natif
    if (isButtonTag) {
      elementProps.type = type;
      elementProps.disabled = finalDisabled;
    }

    // Lien natif
    if (isAnchorTag) {
      elementProps.href = href;
      if (target) {
        elementProps.target = target;
        elementProps.rel =
          rel || (target === "_blank" ? "noopener noreferrer" : undefined);
      }
    }

    // Composant React (Link/NavLink…)
    if (!isButtonTag && !isAnchorTag && to != null) {
      elementProps.to = to;
    }
    if (!isButtonTag && !isAnchorTag && href != null) {
      // si le composant accepte href
      elementProps.href = href;
    }

    return React.createElement(
      Tag,
      elementProps,
      loading ? (
        <Spinner size={size} />
      ) : (
        <>
          {IconLeft && <IconLeft className={iconClass} aria-hidden="true" />}
          {content && <span className="whitespace-nowrap">{content}</span>}
          {IconRight && <IconRight className={iconClass} aria-hidden="true" />}
        </>
      )
    );
  }
);

export default Button;
